import { getMember } from '@vista-digital/occ-domain'
import { hasGivenConsent } from '@/common/utils/cookies/hasGivenConsent'
import { gtm } from '../gtm.service'

const loginStateSnapshotKey = 'gps_login_state_snapshot'

interface LoginStateSnapshot {
    isLoggedIn: boolean
    customerId: string | null
}

const buildLoginStateSnapshot = (isLoggedIn: boolean, customerId: string | null): LoginStateSnapshot => ({
    isLoggedIn,
    customerId,
})

const resolveCustomerId = (isLoggedIn: boolean, memberId?: string | null): string | null => {
    const resolvedMemberId = memberId ?? getMember()?.id
    if (!isLoggedIn || !resolvedMemberId || !hasGivenConsent()) {
        return null
    }

    return resolvedMemberId
}

const getPreviousSnapshot = (): LoginStateSnapshot | null => {
    const previousSnapshotRaw = sessionStorage.getItem(loginStateSnapshotKey)
    if (!previousSnapshotRaw) return null

    try {
        return JSON.parse(previousSnapshotRaw) as LoginStateSnapshot
    } catch {
        return null
    }
}

const isSameSnapshot = (left: LoginStateSnapshot, right: LoginStateSnapshot): boolean =>
    left.isLoggedIn === right.isLoggedIn && left.customerId === right.customerId

/**
 * Fires the login_state GTM event once per session
 * Checks session storage to ensure it only fires once per browsing session
 * @param isLoggedIn - whether the user is currently logged in
 * @param memberId - the member ID if logged in (will be null if not logged in or no consent)
 */
export function trackLoginState(isLoggedIn: boolean, memberId?: string | null): void {
    try {
        const customerId = resolveCustomerId(isLoggedIn, memberId)
        const currentSnapshot = buildLoginStateSnapshot(isLoggedIn, customerId)
        const previousSnapshot = getPreviousSnapshot()
        if (previousSnapshot && isSameSnapshot(previousSnapshot, currentSnapshot)) return

        // Determine authentication method
        const authenticationMethod = isLoggedIn ? 'email' : null

        // Fire the event
        gtm.customEvents.loginState(isLoggedIn, customerId, authenticationMethod)

        // Persist last emitted state for de-duplication.
        sessionStorage.setItem(loginStateSnapshotKey, JSON.stringify(currentSnapshot))
    } catch {
        // Silently fail to avoid disrupting page load
        // Error handling is implicit
    }
}
